import API from '../apiClient';
import { getAuthClient } from './AiModels';

/**
 * Knowledge Library: the platform registry of law, guidance and Zavior notes the Zavvy
 * industry experts cite.
 *
 * IAM /api/v1/knowledge-library (model knowledge_library):
 *   GET    ""            list, filters tier, jurisdiction, industry, framework, status, doc_type
 *   POST   ""            create an upload (git rows only arrive through the import script)
 *   GET    /:id
 *   PUT    /:id          patch: fields left out keep their values; git rows answer 409
 *   DELETE /:id          soft delete (POST /delete/:id is the CORS fallback); git rows answer 409
 *   GET    /family/:family_key   every version and amendment of one law, newest first (ZVR-IAM-0048)
 *
 * AI backend, platform admin only (Super Admin or Tech Admin):
 *   POST /api/v1/library/sync     202 { run_id } or 200 with the counts
 *   GET  /api/v1/library/status   { provider, cursor, items }
 */

const BASE = '/knowledge-library';
const FILTER_KEYS = ['tier', 'jurisdiction', 'industry', 'framework', 'status', 'doc_type'];

export const getKnowledgeLibraryItems = (filters = {}) => {
  const params = {};
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') params[key] = value;
  });
  return API.get(BASE, { params });
};

export const getKnowledgeLibraryItem = (id) => API.get(`${BASE}/${encodeURIComponent(id)}`);

export const createKnowledgeLibraryItem = (body) => API.post(BASE, body);

/** The versions and amendments of one law family (ZVR-IAM-0048). */
export const getKnowledgeLibraryFamily = (familyKey) => API.get(`${BASE}/family/${encodeURIComponent(familyKey)}`);

/** PUT is a patch: send only the fields that changed. */
export const updateKnowledgeLibraryItem = (id, patch) => API.put(`${BASE}/${encodeURIComponent(id)}`, patch);

/**
 * DELETE, falling back to POST /delete/:id only on a network or CORS failure: no response at
 * all, and not the apiClient's silent no-access marker. Any HTTP answer (403, 404, 409) is final.
 */
export const deleteKnowledgeLibraryItem = async (id) => {
  try {
    return await API.delete(`${BASE}/${encodeURIComponent(id)}`);
  } catch (error) {
    if (error?.response || error?.silent) throw error;
    return API.post(`${BASE}/delete/${encodeURIComponent(id)}`);
  }
};

export const syncKnowledgeLibrary = () => getAuthClient().post('/api/v1/library/sync');

export const getKnowledgeLibrarySyncStatus = () => getAuthClient().get('/api/v1/library/status');

/**
 * The message to show for a failed IAM call: a 400 lists the problems IAM found, a 409 is the
 * conflict IAM reports (a git row, or a library_key already in use).
 */
export const knowledgeLibraryErrorMessage = (error, fallback = 'Something went wrong') => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  if (status === 400 && Array.isArray(data.problems) && data.problems.length) {
    return `Please fix: ${data.problems.join('; ')}`;
  }
  if (status === 403) return 'You do not have permission to do this.';
  return data.error || data.message || fallback;
};
